import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {QuestionOptions} from '../../../constants/question-options';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {Proposal} from 'src/app/model/proposal/proposal';
import {PreviousDamage} from '../../../model/proposal/previous-damage';
import {CustomValidators} from '../../../validator/custom-validator';
import {ReplaySubject, startWith, takeUntil} from 'rxjs';
import {BinaryData} from '../../../model/proposal/binary-data';
import {UploadLocation} from '../../../enum/upload-location';
import {UploadComponent} from '../../upload-field/upload.component';

@Component({
    selector: 'app-previous-damage-form',
    templateUrl: './previous-damage-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: PreviousDamageFormComponent }],
    standalone: false
})
export class PreviousDamageFormComponent extends ProposalFormBase implements OnInit, AfterViewInit {

  @Input() proposalPreview: Proposal;

  @ViewChild(UploadComponent) uploadComponent: UploadComponent;
  maxNumber = 5;
  QuestionOptions = QuestionOptions;
  currencyOptions: string[] = QuestionOptions.currency;

  previousDamageSearchArray: PreviousDamageSearch[] = [];

  formGroup = this.fb.group({
    previousDamageExists: false,
    previousDamages: this.fb.array<FormGroup<PreviousDamageForm>>([]),
    previousDamageFiles: new FormControl<BinaryData[]>([]),
  });
  UploadLocation = UploadLocation;

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  setupPreviousDamageExists() {
    this.formGroup.controls.previousDamageExists.valueChanges
      .pipe(startWith(this.formGroup.value.previousDamageExists),
        takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.addForm();
          this.formGroup.controls.previousDamages.enable();
        } else {
          if (this.formGroup.value.previousDamages) {
            this.formGroup.value.previousDamages.forEach((index) => {
                this.deleteForm(index);
              }
            );
          }

          this.formGroup.controls.previousDamages.disable();
        }
      });
    this.formGroup.controls.previousDamageExists.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.setupPreviousDamageExists();
  }


  addForm() {
    const newFormGroup = this.fb.group<PreviousDamageForm>({
      id: new FormControl<string>(null),
      year: new FormControl<number>(null, [CustomValidators.validNumber({allowZero: false})]),
      amountPaid: new FormControl<number>(null, [CustomValidators.validNumber({allowZero: true})]),
      currency: new FormControl<string>('EUR'),
      type: new FormControl<string>('')
    });
    this.formGroup.controls.previousDamages.push(newFormGroup);

    let search = new PreviousDamageSearch();

    search.filteredOptionsCurrency.next(this.currencyOptions);
    search.filterCtrlCurrency.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCurrencies(search.filterCtrlCurrency.value, this.currencyOptions, search.filteredOptionsCurrency);
      });

    this.previousDamageSearchArray.push(search);
  }

  updateProposal(proposal: Proposal): void {
    proposal.previousDamages = [];
    if (this.formGroup.value.previousDamageExists) {
      (this.formGroup.value.previousDamages ?? []).forEach(value => proposal.previousDamages.push(new PreviousDamage(value)));
    }
    (this.formGroup.value.previousDamageFiles ?? []).forEach(file => {
      file.uploadLocation = UploadLocation.PREVIOUSDAMAGE;
      proposal.proposalInformation.attachedFiles.push(file);
    });
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.previousDamages != null && proposal.previousDamages.length > 0) {
      this.formGroup.controls.previousDamageExists.patchValue(true);

      for (let i = 0; i < proposal.previousDamages.length; i++) {
        this.formGroup.controls.previousDamages.at(i).patchValue(proposal.previousDamages[i]);
        if (proposal.proposalInformation.attachedFiles != null) {
          this.uploadComponent.addPreUploadedFiles(
            proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.PREVIOUSDAMAGE)
          );
        }

        if (i + 1 < proposal.riskLocations.length) {
          this.addForm();
        }
      }
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if (proposal.previousDamages != null && proposal.previousDamages.length > 0) {
      this.formGroup.controls.previousDamageExists.patchValue(true);

      for (let i = 0; i < proposal.previousDamages.length; i++) {
        this.formGroup.controls.previousDamages.at(i).patchValue(proposal.previousDamages[i]);
        if (i + 1 < proposal.riskLocations.length) {
          this.addForm();
        }
      }

      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.PREVIOUSDAMAGE)
        );
      }
    }
  }

  deleteForm(i): void {
    this.formGroup.controls.previousDamages.removeAt(i);
    this.previousDamageSearchArray.splice(i);

  }

  attachFiles(previousDamageFiles: BinaryData[]): void {
    this.formGroup.patchValue({
      previousDamageFiles: previousDamageFiles
    });
  }

  getYearList(): number[] {
    let year = (new Date()).getFullYear();
    let years = [];
    for (let i = year - 10; i <= year; i++) {
      years.push(i);
    }

    return years;
  }

  filterCurrencies(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(country => country.toLowerCase().indexOf(search) > -1)
    );
  }

  ngAfterViewInit(): void {
    if (this.proposalPreview?.previousDamages != null) {

      if (this.proposalPreview?.previousDamages.length > 0) {
        this.formGroup.controls.previousDamageExists.patchValue(true);

        for (let i = 0; i < this.proposalPreview.previousDamages.length; i++) {
          this.formGroup.controls.previousDamages.at(i).patchValue(this.proposalPreview.previousDamages[i]);

          if (i + 1 < this.proposalPreview.riskLocations.length) {
            this.addForm();
          }
        }
      }

      if (this.proposalPreview.proposalInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
            this.proposalPreview.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.PREVIOUSDAMAGE)
        );
      }
    }
  }

  reset() {
    this.formGroup.reset();
    this.uploadComponent.clear();
  }
}

interface PreviousDamageForm {
  id: FormControl<string>;
  year: FormControl<number>;
  amountPaid: FormControl<number>;
  currency: FormControl<string>;
  type: FormControl<string>;


}

class PreviousDamageSearch {

  public filterCtrlCurrency: FormControl<string> = new FormControl<string>('');

  public filteredOptionsCurrency: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

}
